/**
 * Hero banner — under-header chrome + optional multi-slide carousel.
 *
 * Progress uses the Progress slider pattern: one segmented bar per slide,
 * with the active segment filling over the autoplay duration.
 * --zaoud-header-height is locked to the *expanded* header size so the
 * under-header pull-up does not jump when sticky compact mode (or a
 * mobile URL-bar resize) remeasures a shorter header mid-scroll.
 */
(function () {
  const root = document.documentElement;
  let hero = null;
  let announcement = null;
  let headerWrapper = null;
  let headerSection = null;
  let lockedHeaderHeight = null;
  let ticking = false;

  /* ------------------------------------------------------------------ */
  /* Under-header measurement                                           */
  /* ------------------------------------------------------------------ */

  function qsHero() {
    return document.querySelector('[data-zaoud-hero-banner][data-under-header="true"]');
  }

  function isHeaderCompact() {
    return Boolean(headerSection && headerSection.classList.contains('scrolled-past-header'));
  }

  function measureExpandedHeaderHeight() {
    if (!headerWrapper) return 0;

    if (!isHeaderCompact()) {
      return Math.round(headerWrapper.getBoundingClientRect().height);
    }

    /* Temporarily expand so we never lock the compact (~20px shorter) size */
    headerSection.classList.remove('scrolled-past-header');
    const height = Math.round(headerWrapper.getBoundingClientRect().height);
    headerSection.classList.add('scrolled-past-header');
    return height;
  }

  function measure() {
    announcement = document.querySelector('.announcement-bar-section');
    headerWrapper = document.querySelector('.header-wrapper');
    headerSection = document.querySelector('.section-header');
    hero = qsHero();

    const announcementHeight = announcement ? Math.round(announcement.getBoundingClientRect().height) : 0;
    const expandedHeight = measureExpandedHeaderHeight();

    if (expandedHeight > 0) {
      /* Keep the tallest stable value — never shrink the pull-up mid-scroll */
      if (lockedHeaderHeight == null || (!isHeaderCompact() && expandedHeight >= lockedHeaderHeight)) {
        lockedHeaderHeight = expandedHeight;
      } else if (!isHeaderCompact() && Math.abs(expandedHeight - lockedHeaderHeight) > 2) {
        lockedHeaderHeight = expandedHeight;
      }
    }

    if (announcement && announcementHeight > 0) {
      root.style.setProperty('--zaoud-announcement-height', `${announcementHeight}px`);
    } else if (!announcement) {
      root.style.setProperty('--zaoud-announcement-height', '0px');
    }

    const nextHeaderHeight = lockedHeaderHeight || expandedHeight;
    if (nextHeaderHeight > 0) {
      root.style.setProperty('--zaoud-header-height', `${nextHeaderHeight}px`);
    }

    if (hero) {
      root.classList.add('zaoud-hero-under-header');
      if (hero.getAttribute('data-light-header') === 'true') {
        root.classList.add('zaoud-hero-header-light');
      } else {
        root.classList.remove('zaoud-hero-header-light');
      }
    } else {
      lockedHeaderHeight = null;
      root.classList.remove('zaoud-hero-under-header', 'zaoud-hero-header-light', 'zaoud-hero-header-solid');
    }

    updateSolidState();
  }

  function updateSolidState() {
    hero = hero || qsHero();
    if (!hero) return;

    headerWrapper = headerWrapper || document.querySelector('.header-wrapper');
    headerSection = headerSection || document.querySelector('.section-header');
    const heroBottom = hero.getBoundingClientRect().bottom;
    /* Prefer locked tall height while over the hero (header stays expanded there) */
    const visibleHeaderHeight =
      lockedHeaderHeight ||
      (headerWrapper ? headerWrapper.getBoundingClientRect().height : 0);
    const pastHero = heroBottom <= visibleHeaderHeight + 1;

    /*
     * Keep the sticky / scroll-up header transparent (gradient + light text).
     * Solid scheme background is CSS-only: hover, focus, open mega menu.
     */
    root.classList.remove('zaoud-hero-header-solid');

    /*
     * StickyHeader adds scrolled-past-header early and shrinks the bar (~20px).
     * That reflow shifts the under-header hero image. Keep the expanded header
     * until we've scrolled past the hero (our scroll listener runs after StickyHeader).
     */
    if (!pastHero && headerSection) {
      headerSection.classList.remove('scrolled-past-header');
    }
  }

  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      measure();
      ticking = false;
    });
  }

  function initHeader() {
    measure();
    window.addEventListener('scroll', updateSolidState, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }

    /* Recalculate after sticky-header / images settle */
    setTimeout(measure, 100);
    setTimeout(measure, 400);
  }

  /* ------------------------------------------------------------------ */
  /* Carousel                                                           */
  /* ------------------------------------------------------------------ */

  class ZaoudHeroCarousel {
    constructor(el) {
      this.root = el;
      this.track = el.querySelector('[data-zhb-track]');
      this.slides = Array.from(el.querySelectorAll('[data-zhb-slide]'));
      this.dots = Array.from(el.querySelectorAll('[data-zhb-dot]'));
      this.fills = Array.from(el.querySelectorAll('[data-zhb-fill]'));

      if (!this.track || this.slides.length <= 1) return;

      this.count = this.slides.length;
      this.index = 0;
      this.speed = (parseInt(el.dataset.speed, 10) || 5) * 1000;
      this.autoplay = el.dataset.autoplay === 'true';
      this.pauseOnHover = el.dataset.pauseOnHover !== 'false';
      this.timer = null;
      this.paused = false;
      this.touchStartX = 0;
      this.touchDeltaX = 0;
      this.swiping = false;

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onDotClick = this.onDotClick.bind(this);

      this.root.addEventListener('pointerenter', this.onPointerEnter);
      this.root.addEventListener('pointerleave', this.onPointerLeave);
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);
      this.root.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.root.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.root.addEventListener('touchend', this.onTouchEnd);
      this.root.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      this.root.setAttribute('tabindex', '0');
      this.show(0, false);
      if (this.autoplay) this.play();
    }

    destroy() {
      this.pause();
      this.root.removeEventListener('pointerenter', this.onPointerEnter);
      this.root.removeEventListener('pointerleave', this.onPointerLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      this.root.removeEventListener('touchstart', this.onTouchStart);
      this.root.removeEventListener('touchmove', this.onTouchMove);
      this.root.removeEventListener('touchend', this.onTouchEnd);
      this.root.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    }

    onDotClick(event) {
      const index = parseInt(event.currentTarget.dataset.zhbDot, 10);
      if (Number.isNaN(index)) return;
      this.show(index, true);
      if (this.autoplay) {
        this.paused = false;
        this.play();
      } else {
        this.runFill(false);
      }
    }

    onPointerEnter() {
      if (!this.autoplay || !this.pauseOnHover) return;
      this.paused = true;
      this.pause();
    }

    onPointerLeave() {
      if (!this.autoplay || !this.pauseOnHover) return;
      this.paused = false;
      this.play();
    }

    onFocusIn(event) {
      if (!this.autoplay) return;
      // Dot clicks receive focus — don't treat pagination as a pause trigger.
      if (event.target.closest('.zaoud-hero-banner__dots, [data-zhb-dot]')) return;
      this.paused = true;
      this.pause();
    }

    onFocusOut(event) {
      if (!this.autoplay) return;
      if (this.root.contains(event.relatedTarget)) return;
      this.paused = false;
      this.play();
    }

    onVisibility() {
      if (!this.autoplay) return;
      if (document.hidden) {
        this.pause();
      } else if (!this.paused) {
        this.play();
      }
    }

    onTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchDeltaX = 0;
      this.swiping = true;
      if (this.autoplay) {
        this.pause();
      }
    }

    onTouchMove(event) {
      if (!this.swiping || !event.touches || event.touches.length !== 1) return;
      this.touchDeltaX = event.touches[0].clientX - this.touchStartX;
    }

    onTouchEnd() {
      if (!this.swiping) return;
      this.swiping = false;
      const threshold = 48;
      if (this.touchDeltaX <= -threshold) {
        this.show(this.index + 1, true);
      } else if (this.touchDeltaX >= threshold) {
        this.show(this.index - 1, true);
      }
      this.touchDeltaX = 0;
      if (this.autoplay) this.play();
    }

    onKeyDown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + 1, true);
        if (this.autoplay) this.play();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.index - 1, true);
        if (this.autoplay) this.play();
      }
    }

    show(index, animate) {
      this.index = ((index % this.count) + this.count) % this.count;
      this.track.classList.toggle('zaoud-hero-banner__track--animate', animate);
      this.track.style.transform = 'translate3d(-' + this.index * 100 + '%, 0, 0)';
      this.updateUI();
    }

    next() {
      this.show(this.index + 1, true);
      if (this.autoplay && !this.paused) this.play();
    }

    play() {
      this.pause();
      this.runFill(true);
      this.timer = window.setTimeout(() => this.next(), this.speed);
    }

    pause() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    runFill(animateActive) {
      this.fills.forEach((fill, i) => {
        fill.style.transition = 'none';
        fill.style.transform = i < this.index ? 'scaleX(1)' : 'scaleX(0)';
      });

      const fill = this.fills[this.index];
      if (!fill) return;

      if (!animateActive) return;

      // Wait one frame so the active dot width transition can start before fill animates.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.fills[this.index] !== fill) return;
          fill.style.transition = 'transform ' + this.speed + 'ms linear';
          fill.style.transform = 'scaleX(1)';
        });
      });
    }

    updateUI() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        const complete = i < this.index;
        dot.classList.toggle('is-active', active);
        dot.classList.toggle('is-complete', complete);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (!this.autoplay) {
        this.fills.forEach((fill, i) => {
          fill.style.transition = 'none';
          fill.style.transform = i <= this.index ? 'scaleX(1)' : 'scaleX(0)';
        });
      }
    }
  }

  const carouselInstances = new WeakMap();

  function bootCarousels(scope) {
    scope.querySelectorAll('[data-zhb-carousel]').forEach((el) => {
      const prev = carouselInstances.get(el);
      if (prev) {
        prev.destroy();
        carouselInstances.delete(el);
      }

      const carousel = new ZaoudHeroCarousel(el);
      if (carousel.track && carousel.slides && carousel.slides.length > 1) {
        carouselInstances.set(el, carousel);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    initHeader();
    bootCarousels(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    measure();
    bootCarousels(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    measure();
    event.target.querySelectorAll('[data-zhb-carousel]').forEach((el) => {
      const carousel = carouselInstances.get(el);
      if (carousel) {
        carousel.destroy();
        carouselInstances.delete(el);
      }
    });
  });
  document.addEventListener('shopify:section:reorder', measure);
})();
